/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-promise-executor-return */
// src/hooks/useMediasoup.js
import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import * as mediasoupClient from 'mediasoup-client';

export function useMediasoup(userId) {
  const localVideo = useRef(null);
  const remoteVideos = useRef([]); // liste pour les autres participants
  const socketRef = useRef(null);
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_BASE_URL, {
      query: { userId }
    });
    socketRef.current = socket;

    let device;

    (async () => {
      // 1) Charger capacités RTP depuis le serveur
      const rtpCapabilities = await new Promise((resolve) => socket.emit('getRtpCapabilities', null, resolve));

      device = new mediasoupClient.Device();
      await device.load({ routerRtpCapabilities: rtpCapabilities });
      deviceRef.current = device;

      // 2) Créer transport pour envoyer flux
      const sendTransportData = await new Promise((resolve) => socket.emit('createTransport', null, resolve));

      const sendTransport = device.createSendTransport(sendTransportData);
      sendTransportRef.current = sendTransport;

      sendTransport.on('connect', ({ dtlsParameters }, callback) => {
        socket.emit('connectTransport', {
          transportId: sendTransport.id,
          dtlsParameters
        });
        callback();
      });

      sendTransport.on('produce', (params, callback) => {
        socket.emit('produce', {
          transportId: sendTransport.id,
          kind: params.kind,
          rtpParameters: params.rtpParameters
        }, ({ id }) => callback({ id }));
      });

      // 3) Publier la webcam
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      if (localVideo.current) localVideo.current.srcObject = stream;
      const track = stream.getVideoTracks()[0];
      await sendTransport.produce({ track });

      // 4) Gérer arrivée d'un nouveau producteur
      socket.on('newProducer', async ({ producerId }) => {
        // si pas encore de transport de réception
        if (!recvTransportRef.current) {
          const recvTransportData = await new Promise((resolve) => socket.emit('createTransport', null, resolve));
          const recvTransport = device.createRecvTransport(recvTransportData);
          recvTransportRef.current = recvTransport;

          recvTransport.on('connect', ({ dtlsParameters }, callback) => {
            socket.emit('connectTransport', {
              transportId: recvTransport.id,
              dtlsParameters
            });
            callback();
          });
        }

        // Consommer ce producteur
        const consumerData = await new Promise((resolve) => socket.emit('consume', {
          transportId: recvTransportRef.current.id,
          producerId,
          rtpCapabilities: device.rtpCapabilities
        }, resolve));

        const consumer = await recvTransportRef.current.consume({
          id: consumerData.id,
          producerId: consumerData.producerId,
          kind: consumerData.kind,
          rtpParameters: consumerData.rtpParameters
        });

        const newStream = new MediaStream();
        newStream.addTrack(consumer.track);

        // ajouter à la liste de remoteVideos
        remoteVideos.current.push(newStream);
      });
    })();

    return () => {
      socket.disconnect();
    };
  }, [userId]);

  return { localVideo, remoteVideos };
}
